import { TKeyValuePair } from 'library/ts/types/TKeyValuePair';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { EmbedDevice } from 'tag/ts/tag/model/type/embed/EmbedDevice';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { ComcastCapabilitiesExtension } from 'tag/ts/tag/player/adapters/avia/extensions/capabilities/html5/ComcastCapabilitiesExtension';
import { LgCapabilitiesExtension } from 'tag/ts/tag/player/adapters/avia/extensions/capabilities/html5/LgCapabilitiesExtension';
import { SamsungCapabilitiesExtension } from 'tag/ts/tag/player/adapters/avia/extensions/capabilities/html5/SamsungCapabilitiesExtension';
import { XboxCapabilitiesExtension } from 'tag/ts/tag/player/adapters/avia/extensions/capabilities/html5/XboxCapabilitiesExtension';
import { HdcpCapabilitiesExtension } from 'tag/ts/tag/player/adapters/avia/extensions/capabilities/utility/HdcpCapabilitiesExtension';
import { IAviaExtension } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaExtension';
import { IAviaExtensionsManager } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaExtensionsManager';

/**
 * @copyright © Paramount Streaming
 * @author  Jeff Appis
 * @description VTG Avia platform capabilities extension manager for cross-platform deployment
 *
 * **Platform Capabilities Orchestration:**
 * Manages VTG Avia extensions that provide platform-specific capabilities for Smart TV,
 * gaming console, and set-top box deployments. Extensions enable optimized video playback
 * and DRM support across different hardware platforms and embedded environments.
 *
 * **Managed Platform Extensions:**
 * - **HDCP Extension**: Content protection for DRM on Xbox, PlayStation, Comcast, Desktop
 * - **LG webOS Extension**: Smart TV capabilities for LG platform optimization
 * - **Samsung Tizen Extension**: Smart TV capabilities for Samsung platform optimization
 * - **Xbox One Extension**: Gaming console capabilities for Microsoft Xbox platform
 * - **Comcast Extension**: Cable/set-top box capabilities for Comcast X1 platform
 *
 * **Extension Criticality Assessment:**
 * Uses device detection to determine which extensions are critical for the current platform:
 * - **Critical Extensions**: Required for core functionality on specific platforms
 * - **Optional Extensions**: Enhanced capabilities that improve but don't block functionality
 * - **Error Handling**: Platform-specific error recovery for extension failures
 *
 * **Device-Specific Logic:**
 * - **Smart TV Platforms**: LG webOS, Samsung Tizen extensions for optimal TV playback
 * - **Gaming Consoles**: Xbox One capabilities for console-specific streaming optimization
 * - **Cable Platforms**: Comcast X1 integration for set-top box deployment
 * - **DRM Platforms**: HDCP support for content protection across supported devices
 *
 * **Registration Strategy:**
 * Extensions are conditionally loaded based on platform detection and business logic,
 * ensuring optimal performance and capability utilization for each deployment target.
 *
 * **Business Impact:**
 * Enables Smart Tag video service to operate optimally across diverse platform ecosystem
 * including Smart TVs, gaming consoles, cable boxes, and desktop environments.
 */
export class AviaExtensionsManager extends AbstractTagClass implements IAviaExtensionsManager {

	get #allExtensions(): IAviaExtension[] {
		return [
			...this.#capabilitiesExtensions
		];
	}

	get #capabilitiesExtensions(): IAviaExtension[] {
		return [
			new LgCapabilitiesExtension( { tag: this?.tag } ),
			new XboxCapabilitiesExtension( { tag: this?.tag } ),
			new SamsungCapabilitiesExtension( { tag: this?.tag } ),
			new ComcastCapabilitiesExtension( { tag: this?.tag } ),
			new HdcpCapabilitiesExtension( { tag: this?.tag } )
		];
	}

	/**
	 * Debug flags for use during development.
	 * @returns {TKeyValuePair}
	 */
	get #debugging(): TKeyValuePair {
		if ( !this.runtimeVO?.debug?.on ) {
			return {};
		}

		return {
			forceAllExtensionsToLoad: false
		};
	}

	constructor( argument: ITagAbstractClassArgument ) {
		super( {
			...argument,
			name: 'Avia Extensions Manager'
		} );
	}

	loadCapabilitiesExtensions(): Promise<boolean> {
		return this.#registerExtensions( this.#capabilitiesExtensions );
	}

	loadAllExtensions(): Promise<boolean> {
		return this.#registerExtensions( this.#allExtensions );
	}

	#registerExtensions( roster: IAviaExtension[] ): Promise<boolean> {
		const extensionPromises: Array<{ extension: IAviaExtension, promise: Promise<void> }> = [];
		const failures: Array<{ extension: string, error: any, critical: boolean }> = [];
		const criticalFailures: string[] = [];

		roster
			.filter( ( extension ) => {
				const extensionShouldLoad = extension.shouldBeUsed() || this.#debugging?.forceAllExtensionsToLoad;
				if ( !extensionShouldLoad ) {
					this?.logger?.log( 'Extension', extension.name, ' will not be loaded' );
				}
				return extensionShouldLoad;
			} )
			.forEach( ( extension ) => {
				extensionPromises.push( {
					extension: extension,
					promise: extension.register().catch( ( error ) => {
						const isCritical = this.#isCriticalExtension( extension.name );

						failures.push( {
							extension: extension.name,
							error: error,
							critical: isCritical
						} );

						if ( isCritical ) {
							criticalFailures.push( extension.name );
							this?.logger?.error( 'CRITICAL extension registration failed:', extension.name, error );
						} else {
							this?.logger?.warn( 'Optional extension registration failed:', extension.name, error );
						}

						// Continue processing other extensions
						return Promise.resolve();
					} )
				} );
			} );

		this?.logger?.log( 'registerExtensions', 'extensionsLoading', extensionPromises.length );

		// Use allSettled to get results of all extensions, even if some fail
		return Promise.allSettled( extensionPromises.map( ( ep ) => ep.promise ) ).then( ( results ) => {
			const successful = results.filter( ( r ) => r.status === 'fulfilled' ).length;

			// Log summary
			if ( failures.length > 0 ) {
				this?.logger?.warn( 'Extension registration summary:', {
					successful: successful,
					failed: failures.length,
					criticalFailures: criticalFailures.length,
					failures: failures
				} );
			} else {
				this?.logger?.log( 'Extension loading complete. All extensions loaded successfully.' );
			}

			// Return false only if critical extensions failed
			if ( criticalFailures.length > 0 ) {
				this?.logger?.error( `Critical extensions failed to register: ${criticalFailures.join( ', ' )}` );
				return false;
			}

			return true;
		} );
	}

	/**
	 * @description Determines if an extension is critical for the current device/platform
	 * @param extensionName
	 * @returns boolean
	 */
	#isCriticalExtension = ( extensionName: string ): boolean => {
		const currentDevice = this.tag.params?.SYSTEM?.device;

		// HDCP is critical for DRM content on supporting devices
		if ( extensionName === 'HdcpCapabilitiesExtension' ) {
			const isHDCPDevice = [
				EmbedDevice.XBOX_ONE,
				EmbedDevice.PS4,
				EmbedDevice.PS5,
				EmbedDevice.COMCAST,
				EmbedDevice.COX,
				EmbedDevice.VIDAA,
				EmbedDevice.DESKTOP
			].includes( currentDevice );

			// Critical only if we're on an HDCP device
			return isHDCPDevice;
		}

		// Platform-specific extensions are critical only on their respective platforms
		switch ( extensionName ) {
			case 'XboxCapabilitiesExtension':
				return currentDevice === EmbedDevice.XBOX_ONE;
			case 'ComcastCapabilitiesExtension':
				return currentDevice === EmbedDevice.COMCAST;
			case 'SamsungCapabilitiesExtension':
			case 'LgCapabilitiesExtension':
				// These are critical on their respective smart TV platforms
				return this.params?.SYSTEM?.embedType === EmbedType.HTML5;
			default:
				return false;
		}
	};
}